import { useState } from 'react';
import type { DrillRow, ScoringMode } from '../lib/api';
import { formatMagazines } from '../lib/magazines';
import { errorText } from '../lib/errorText';

interface Props {
  drill: DrillRow;
  mode: ScoringMode;
  onConfirm: (distanceCm: number) => Promise<void> | void;
  onBack: () => void;
}

/**
 * Step 3 of the round-setup flow: referee picks the distance to
 * target. Defaults to the drill's suggested distance (when set).
 *
 * The quick-pick chips cover the standard club distances. "Andere…"
 * opens a numeric input for arbitrary values (1–300 m, decimal-comma
 * supported). Storage is integer cm so 12,5 m round-trips as 1250
 * without floats.
 */
const QUICK_PICKS_CM = [300, 500, 700, 1000, 1250, 1500, 2500];

function fmtMeters(cm: number): string {
  const m = cm / 100;
  return `${m.toLocaleString('de-AT', { maximumFractionDigits: 2 })} m`;
}

function parseCustomMeters(raw: string): number | null {
  const cleaned = raw.replace(',', '.').trim();
  if (cleaned === '') return null;
  const m = Number.parseFloat(cleaned);
  if (!Number.isFinite(m) || m <= 0 || m > 300) return null;
  return Math.round(m * 100);
}

export function DistancePicker({ drill, mode, onConfirm, onBack }: Props) {
  const initialCm = drill.drill.distanceCm ?? null;
  const initialMatchesQuickPick = initialCm != null && QUICK_PICKS_CM.includes(initialCm);
  const [pickedCm, setPickedCm] = useState<number | null>(
    initialMatchesQuickPick ? initialCm : initialCm,
  );
  const [showCustom, setShowCustom] = useState(initialCm != null && !initialMatchesQuickPick);
  const [customRaw, setCustomRaw] = useState<string>(
    initialCm != null && !initialMatchesQuickPick
      ? (initialCm / 100).toLocaleString('de-AT', { maximumFractionDigits: 2 })
      : '',
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const customCm = showCustom ? parseCustomMeters(customRaw) : null;
  const effectiveCm = showCustom ? customCm : pickedCm;
  const canStart = effectiveCm != null && effectiveCm > 0;

  async function start() {
    if (!canStart || effectiveCm == null) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm(effectiveCm);
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }

  return (
    <section className="space-y-4">
      <header className="flex items-center justify-between">
        <button onClick={onBack} className="min-h-12 pr-3 text-sm text-zinc-500 underline">
          ← zurück
        </button>
        <h2 className="text-lg font-bold">Distanz wählen</h2>
      </header>

      <p className="text-sm text-zinc-400">
        <span className="font-medium text-zinc-200">{drill.drill.name}</span> ·{' '}
        {mode === 'speed' ? 'Speed' : 'Präzision'} · {drill.drill.shotCount} Schuss
        {formatMagazines(drill.drill.magazines)
          ? ` (${formatMagazines(drill.drill.magazines)})`
          : ''}
      </p>

      {error && (
        <div role="alert" className="space-y-3 rounded bg-red-900/30 p-3 text-sm text-red-300">
          <p>Fehler: {error}</p>
          <button
            type="button"
            onClick={() => void start()}
            disabled={busy}
            className="min-h-12 rounded-lg border border-red-400/60 px-4 py-2 font-semibold text-red-100 hover:bg-red-900/40 disabled:opacity-50"
          >
            Erneut versuchen
          </button>
        </div>
      )}

      <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
        {QUICK_PICKS_CM.map((cm) => {
          const active = !showCustom && pickedCm === cm;
          return (
            <button
              key={cm}
              type="button"
              onClick={() => {
                setShowCustom(false);
                setPickedCm(cm);
              }}
              className={`rounded-lg border px-3 py-4 text-base font-semibold transition-colors ${
                active
                  ? 'border-emerald-500 bg-emerald-700 text-white'
                  : 'border-zinc-700 bg-zinc-900 text-zinc-200 hover:border-zinc-500'
              }`}
            >
              {fmtMeters(cm)}
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => {
            setShowCustom(true);
            setPickedCm(null);
          }}
          className={`rounded-lg border px-3 py-4 text-base font-semibold transition-colors ${
            showCustom
              ? 'border-emerald-500 bg-emerald-700 text-white'
              : 'border-zinc-700 bg-zinc-900 text-zinc-200 hover:border-zinc-500'
          }`}
        >
          Andere…
        </button>
      </div>

      {showCustom && (
        <label className="block">
          <span className="text-sm text-zinc-400">Distanz in Meter</span>
          <input
            type="text"
            inputMode="decimal"
            value={customRaw}
            onChange={(e) => setCustomRaw(e.target.value)}
            placeholder="z. B. 8,5"
            className="mt-1 w-full rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-3 text-lg font-medium tabular-nums text-zinc-100 focus:border-emerald-500 focus:outline-none"
          />
          {customRaw !== '' && customCm == null && (
            <span className="mt-1 block text-xs text-amber-300">
              Ungültig — bitte 1–300 m, z. B. „8,5".
            </span>
          )}
        </label>
      )}

      <div className="pt-2">
        <button
          type="button"
          onClick={() => void start()}
          disabled={!canStart || busy}
          className="w-full rounded-lg bg-emerald-700 px-4 py-4 text-lg font-bold text-white transition-colors hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {busy ? '⏳ Starte…' : `Starten — ${effectiveCm != null ? fmtMeters(effectiveCm) : '—'}`}
        </button>
      </div>
    </section>
  );
}
